/**
 * @file interpret/engine/micCapture.ts
 * @brief Interpretation mic: voice processing ON, streamed as 20 ms PCM16 frames.
 *
 * @details
 * Call mode (05-10-2026) is the exception: the mic RAW, see CALL_MODE_RAW_MIC.
 *
 * Interpretation is the opposite of the Boardroom recorder
 * (`employee-boardroom/capture/captureProfile.ts`, everything OFF for diarization):
 * - `echoCancellation: true`: our own TTS comes out of the laptop speakers.
 * - `noiseSuppression` + `autoGainControl`: the phone's 'voice' profile
 *   (Apple voice processing) does the same; the server's Silero adaptive gain is
 *   tuned for that kind of input.
 *
 * Echo is not left to AEC alone. Like the phone's Silero screen, the controller
 * closes the GATE while a translation plays and reopens it after the "your turn"
 * beep, so nothing we play reaches the server even when AEC misses.
 *
 * The AudioWorklet only copies mic samples out of the audio thread (batched to
 * ~21 ms); resampling and base64 run on the main thread (`pcm16.ts`), which is
 * cheap at 50 frames a second. The worklet code is loaded from a Blob URL so it
 * needs no bundler step and works the same in the browser and the desktop app.
 *
 * @author DES
 * @date 01-10-2026
 */

import { Pcm16Resampler, S2S_FRAME_SAMPLES, frameRms, int16ToBase64 } from "./pcm16";

export const INTERPRET_CAPTURE_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: 1,
};

/**
 * (05-10-2026) Call mode: the mic RAW, with no browser processing. DES's
 * WhatsApp and Meet tests: your voice broke "toot toot, ooper neeche", in the
 * call and in the session's original audio, while a direct session was clean.
 * The uplink showed every gap between words dropping to a flat -76 dB, below
 * the room itself, 3-4 times a second: the browser's echo canceller and noise
 * suppressor at work. In Call mode the app also PLAYS your live voice (into
 * Verblizr Output), so the echo canceller can take your own voice for echo of
 * what it plays; and the call's audio comes from another app, which it cannot
 * cancel anyway. The call app (Meet, WhatsApp, Teams, Zoom) does its own
 * processing with the right reference. Direct sessions keep the processed mic.
 * Set false to go back.
 */
export const CALL_MODE_RAW_MIC = true;

export const CALL_MODE_CAPTURE_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
  channelCount: 1,
};

export function interpretAudioConstraints(
  deviceId?: string,
  opts?: { callMode?: boolean },
): MediaTrackConstraints {
  const base =
    opts?.callMode && CALL_MODE_RAW_MIC ? CALL_MODE_CAPTURE_CONSTRAINTS : INTERPRET_CAPTURE_CONSTRAINTS;
  return deviceId ? { ...base, deviceId: { exact: deviceId } } : { ...base };
}

export async function openInterpretMic(
  deviceId?: string,
  opts?: { callMode?: boolean },
): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw Object.assign(new Error("This browser cannot use the microphone."), {
      name: "NotSupportedError",
    });
  }
  return navigator.mediaDevices.getUserMedia({
    audio: interpretAudioConstraints(deviceId, opts),
    video: false,
  });
}

const WORKLET_NAME = "vz-pcm-tap";
const WORKLET_SOURCE = `
class VzPcmTap extends AudioWorkletProcessor {
  constructor() {
    super();
    this.buf = new Float32Array(1024);
    this.fill = 0;
  }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch) {
      for (let i = 0; i < ch.length; i++) {
        this.buf[this.fill++] = ch[i];
        if (this.fill === this.buf.length) {
          this.port.postMessage(this.buf, [this.buf.buffer]);
          this.buf = new Float32Array(1024);
          this.fill = 0;
        }
      }
    }
    return true;
  }
}
registerProcessor("${WORKLET_NAME}", VzPcmTap);
`;

export type PcmFrameHandler = (frame: { base64: string; samples: number; rms: number }) => void;

export type PcmCapture = {
  /** Open = frames reach `onFrame`; closed = dropped (playback, pause). */
  setGate(open: boolean): void;
  /** Latest frame level, 0..1, updated even while the gate is closed. */
  level(): number;
  stop(): void;
};

/**
 * Start streaming `stream` as 16 kHz PCM16 frames. Call after a user gesture
 * (Safari keeps a context created elsewhere suspended).
 */
export async function startPcmCapture(
  stream: MediaStream,
  onFrame: PcmFrameHandler,
): Promise<PcmCapture> {
  const ctx = new AudioContext();
  if (ctx.state === "suspended") await ctx.resume();

  const url = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: "text/javascript" }));
  try {
    await ctx.audioWorklet.addModule(url);
  } finally {
    URL.revokeObjectURL(url);
  }

  const source = ctx.createMediaStreamSource(stream);
  const tap = new AudioWorkletNode(ctx, WORKLET_NAME, {
    numberOfInputs: 1,
    numberOfOutputs: 1,
    channelCount: 1,
    channelCountMode: "explicit",
  });
  // A node nobody pulls may never run; route it to the output silently.
  const mute = ctx.createGain();
  mute.gain.value = 0;
  source.connect(tap);
  tap.connect(mute);
  mute.connect(ctx.destination);

  const resampler = new Pcm16Resampler(ctx.sampleRate);
  let open = true;
  let lastRms = 0;
  let stopped = false;

  tap.port.onmessage = (ev: MessageEvent<Float32Array>) => {
    if (stopped) return;
    for (const frame of resampler.push(ev.data)) {
      lastRms = frameRms(frame);
      if (open) onFrame({ base64: int16ToBase64(frame), samples: S2S_FRAME_SAMPLES, rms: lastRms });
    }
  };

  return {
    setGate(next: boolean) {
      if (next && !open) resampler.reset(); // never lead a turn with gated-out audio
      open = next;
    },
    level: () => lastRms,
    stop() {
      if (stopped) return;
      stopped = true;
      tap.port.onmessage = null;
      try {
        source.disconnect();
        tap.disconnect();
        mute.disconnect();
      } catch {
        /* already torn down */
      }
      void ctx.close().catch(() => undefined);
    },
  };
}
